import { FC } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';

import downloadDocument from 'utils/downloadDocument';
import asyncActionSwal from 'utils/asyncActionSwal';
import successSwal from 'utils/successSwal';
import errorSwal from 'utils/errorSwal';

import { Council } from 'types';

import { setModal } from 'modules/_shared/redux/modalActions';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import { useGetDocuments } from 'modules/_shared/hooks/useGetDocuments';

import EmptyStateForTable from 'modules/_shared/components/EmptyStates/EmptyStateForTable';
import DocumentViewer from 'modules/_shared/components/Modals/DocumentViewer';
import sizes from 'constants/sizes';
import DocumentsHeader from 'modules/_shared/components/BoardDocuments/DocumentsHeader';
import DocumentsList from 'modules/_shared/components/BoardDocuments/DocumentsList';

import AddCouncilDocumentModal from './AddCouncilDocumentModal';
import { removeDocumentFromCouncil } from '../utils/removeDocumentFromCouncil';

type Props = {
  council: Council;
  fetchCouncilData: () => void;
  isAdminView?: boolean;
  isSummaryVersion?: boolean;
};

const MembersDocumentsSection: FC<Props> = ({
  council,
  fetchCouncilData,
  isAdminView = true,
  isSummaryVersion = false,
}: Props) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const isReadOnly = false; // TODO: Implement permissions

  const membersDocumentsIds = council?.publicDocuments || [];

  const documentsData = useGetDocuments({ documentIds: membersDocumentsIds });

  const { documents } = documentsData.data;
  const { isLoading } = documentsData;
  const { setIsLoading } = documentsData.actions;

  const hasDocuments = documents.length > 0;

  const handleViewDocument = (index: number) => {
    dispatch(
      setModal(<DocumentViewer document={documents[index]} size={sizes.XL} />)
    );
  };

  const handleDownloadDocument = async (index: number) => {
    downloadDocument({
      documentId: documents[index]?.['_id'],
      documentName: documents[index]?.name,
      documents: [],
    });
  };

  const handleAddDocument = () => {
    if (council) {
      dispatch(
        setModal(
          <AddCouncilDocumentModal
            council={council}
            fetchCouncilData={fetchCouncilData}
          />
        )
      );
    }
  };

  const handleRemoveDocument = async (index: number) => {
    const documentId = documents[index]?._id;
    const documentName = documents[index]?.name;

    asyncActionSwal({
      swalTitle: t('AreYouSureAboutDelDocument'),
      swalElementName: documentName,
      swalInfoText: t('ThisProcedureCantUndo'),
      swalConfirmButtonText: t('OkDelete'),
      callback: async () => {
        try {
          setIsLoading(true);
          if (council) {
            await removeDocumentFromCouncil({
              documentId,
              council,
              privateDocument: false,
            });
          }
          successSwal(t('DocumentDeletedSuccessfully'));
        } catch {
          errorSwal(t('ErrorRemovingDocument'));
        } finally {
          setIsLoading(false);
          fetchCouncilData();
        }
      },
    });
  };

  return (
    <>
      <div
        className={`nk-block-head pb-0 ${
          isSummaryVersion ? 'card-inner documents-card-version' : ''
        }`}
      >
        <div className="nk-block-head-content row sp-plan-head mb-2">
          <h5 className="nk-block-title title mb-0 col-md-6">
            {t('DocumentsForMembers')}
          </h5>
          <div className="col-md-6 text-right nk-block-head-content">
            {isAdminView && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddDocument}
                disabled={isReadOnly}
              >
                <em className="icon ni ni-upload mr-1" />
                {t('UpDocument')}
              </button>
            )}
          </div>
        </div>
      </div>

      {hasDocuments ? (
        <div className="nk-files nk-files-view-list">
          <DocumentsHeader />
          <DocumentsList
            documents={documents}
            handleDownloadDocument={handleDownloadDocument}
            handleRemoveDocument={handleRemoveDocument}
            handleViewDocument={handleViewDocument}
            isAdminView={isAdminView}
            isSummaryVersion={isSummaryVersion}
            textSize={400}
          />
        </div>
      ) : (
        <div className="card bg-white rounded-xl mt-4">
          <EmptyStateForTable
            mainMessage={t('CouncilNoDocumentsForMembers')}
            infoMessage={t('CouncilNoDocumentsForMembersDescription')}
            colspan={4}
          />
        </div>
      )}

      {isLoading && <CustomLoading />}
    </>
  );
};

export default MembersDocumentsSection;
